// src/views/BuildersInfo.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Added useNavigate
import useFindBuilderStore from "../store/useFindBuilderStore.js";
import { ClipLoader } from "react-spinners";
import useAuthStore from "../store/useAuthStore.js"; // Corrected path if needed
import SuccessPopup from "../components/common/SuccessPopup";
import toast from "react-hot-toast";
import {
  MapPinIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  AcademicCapIcon,
  WrenchScrewdriverIcon,
  PhotoIcon,
  EnvelopeIcon,
  UserCircleIcon,
  AtSymbolIcon,
} from "@heroicons/react/24/outline";

const BuildersInfo = () => {
  const {
    builder,
    fetchBuilderInfo,
    isLoading: builderLoading,
    fetchMappedDistricts,
    fetchMappedTaluks,
    mappedDistricts,
    mappedTaluks,
    sendProjectInvite,
  } = useFindBuilderStore();
  const { builderId } = useParams();
  // --- FIX: Destructure userType along with user ---
  const { user, userType } = useAuthStore(); // Get logged-in customer info and their type
  const navigate = useNavigate(); // Initialize useNavigate

  // Form State
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [service, setService] = useState("");
  const [taluk, setTaluk] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [projectLocation, setProjectLocation] = useState("");

  // UI State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  // Fetch initial data
  useEffect(() => {
    window.scrollTo(0, 0);
    if (builderId) {
      fetchBuilderInfo(builderId);
    }
    // Fetch districts only if a customer is logged in, otherwise the form won't show
    if (user && userType === 'customer') {
       fetchMappedDistricts();
    }
  }, [fetchBuilderInfo, builderId, fetchMappedDistricts, user, userType]); // Add user dependencies

  // Fetch taluks when district changes
  useEffect(() => {
    if (selectedDistrict && user && userType === 'customer') { // Check user type again
      fetchMappedTaluks(selectedDistrict);
    } else {
      useFindBuilderStore.setState({ mappedTaluks: [], selectedTaluk: "" });
      setTaluk("");
    }
  }, [selectedDistrict, fetchMappedTaluks, user, userType]); // Add user dependencies

  // Function to clear the enquiry form
  const clearEnquiryForm = () => {
    setPhoneNumber("");
    setService("");
    setSelectedDistrict("");
    setTaluk("");
    setPostalCode("");
    setProjectLocation("");
  };

  // Handle enquiry form submission
  const handleEnquirySubmit = async (e) => {
    e.preventDefault();

    if (!builder || !builder.builder_id) {
      toast.error("Builder information is missing. Cannot send enquiry.");
      return;
    }
     // Redundant check as form won't render, but good practice
    if (!user || userType !== 'customer' || !user.customerId) {
      toast.error("Please log in as a customer to send an enquiry.");
      navigate('/login?type=customer'); // Redirect if somehow user gets here without login
      return;
    }


    setIsSubmitting(true);

    const inviteData = {
      builderId: builder.builder_id,
      customerId: user.customerId, // Use customerId from user object
      projectName: "Enquiry",
      projectBudget: "N/A",
      startingDate: new Date().toISOString().split("T")[0],
      projectLocation: projectLocation || "N/A",
      phoneNumber: phoneNumber || "N/A",
      serviceId: service || null, // Ensure service ID is sent or null
      district: selectedDistrict || "N/A",
      taluk: taluk || "N/A",
      postalCode: postalCode || "N/A",
      customerName: user.name, // Pre-fill from logged-in user
      customerEmail: user.email, // Pre-fill from logged-in user
      customerDucktailId: user.customerId, // Use customerId as ducktail_id
    };

    try {
      const result = await sendProjectInvite(inviteData);
      if (result.success) {
        setPopupMessage(result.message);
        setIsPopupOpen(true);
      }
    } catch (error) {
      console.error("Error submitting project invite:", error);
      toast.error("An unexpected error occurred while sending the enquiry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close popup and clear the form
  const closePopupAndClear = () => {
    setIsPopupOpen(false);
    clearEnquiryForm();
  };

  // Determine grid columns for portfolio
  const portfolioGridCols = () => {
    const count = builder?.portfolios?.length || 0;
    if (count === 0) return "";
    if (count === 1) return "grid-cols-1 max-w-sm mx-auto";
    if (count === 2) return "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto";
    if (count === 3) return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto";
    return "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4";
  };

  // --- Loading State ---
  if (builderLoading && !builder) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <ClipLoader size={50} color={"#3B82F6"} loading={builderLoading} />
        <p className="ml-4 text-gray-600">Loading Builder Information...</p>
      </div>
    );
  }

  // --- No Data State ---
  if (!builderLoading && !builder) {
    return (
      <div className="text-center p-10 text-gray-600 bg-gray-50 min-h-screen flex flex-col items-center justify-center">
        <p className="text-xl font-semibold">Builder Not Found</p>
        <p className="mt-2">We couldn't find the details for this builder ID.</p>
        <button
            onClick={() => navigate('/builderslist')} // Navigate back to the list
            className="mt-6 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
            Back to Builders List
        </button>
      </div>
    );
  }

  // --- Main Content ---
  return (
    <div className="relative p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen font-sans">

      {/* --- Render Success Popup --- */}
      <SuccessPopup
        isOpen={isPopupOpen}
        onClose={closePopupAndClear}
        message={popupMessage}
      />
      {/* --- End Render Success Popup --- */}

      {/* Builder Info Card */}
      <div className="max-w-7xl mx-auto rounded-2xl overflow-hidden bg-white shadow-2xl border border-gray-100">
        <div className="p-6 sm:p-10 lg:p-12">
          {/* Header Section */}
          {/* ... (Header JSX remains the same) ... */}
           <div className="flex flex-col md:flex-row items-center md:items-start mb-10 md:mb-12 gap-6 md:gap-10">
            {/* Profile Image */}
            <div className="w-36 h-36 md:w-48 md:h-48 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 shadow-lg border-4 border-blue-100">
              <img
                src={builder.profile_image || "/assets/placeholder-avatar.png"}
                alt={`${builder.company_name || "Builder"} Profile`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = "/assets/placeholder-avatar.png"; }}
              />
            </div>
            {/* Basic Info */}
            <div className="text-center md:text-left flex-grow mt-4 md:mt-0">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 leading-tight">
                {builder.company_name || "Company Name Not Available"}
              </h1>
              <p className="text-base text-blue-600 font-semibold mb-4 tracking-wide">
                Ducktail ID: {builder.ducktail_id || "N/A"}
              </p>
              {/* Contact & Location Details */}
              <div className="text-sm sm:text-base text-gray-600 space-y-2 mt-4">
                 {builder.engineer_name && (
                     <p className="flex items-center justify-center md:justify-start">
                      <UserCircleIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                      <span>Engineer: {builder.engineer_name}</span>
                    </p>
                 )}
                <p className="flex items-center justify-center md:justify-start">
                  <BuildingOffice2Icon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                  <span>{builder.address_line1 || "Address Line 1 N/A"}</span>
                </p>
                {builder.address_line2 && (
                  <p className="pl-8 text-sm sm:text-base">
                    {builder.address_line2}
                  </p>
                )}
                <p className="flex items-center justify-center md:justify-start">
                  <MapPinIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                  <span>
                    {builder.taluk || "Taluk N/A"}, {builder.district || "District N/A"},{" "}
                    {builder.state || "State N/A"} - {builder.postcode || "N/A"}
                  </span>
                </p>
                 <p className="flex items-center justify-center md:justify-start pt-1">
                    <PhoneIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                    <a href={`tel:${builder.contact_number}`} className="hover:text-blue-600 transition-colors">
                    {builder.contact_number || "Contact N/A"}
                    </a>
                 </p>
                 {builder.email && (
                     <p className="flex items-center justify-center md:justify-start pt-1">
                        <AtSymbolIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                        <a href={`mailto:${builder.email}`} className="hover:text-blue-600 transition-colors truncate" title={builder.email}>
                           {builder.email}
                        </a>
                     </p>
                 )}
                {builder.qualification && (
                  <p className="flex items-center justify-center md:justify-start pt-1">
                    <AcademicCapIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                    <span>{builder.qualification}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Separator */}
          <hr className="border-gray-200 my-10 md:my-12" />

          {/* About Section */}
          {/* ... (About Section JSX remains the same) ... */}
           <div className="mb-12 md:mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6">
              About The Company
            </h2>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto text-justify indent-8">
              {builder.about || "No detailed description provided by the builder."}
            </p>
          </div>

           {/* Services Section */}
          {/* ... (Services Section JSX remains the same) ... */}
          {builder.services && builder.services.length > 0 && (
            <div className="mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8 flex items-center justify-center gap-3">
                 <WrenchScrewdriverIcon className="w-7 h-7 text-blue-500"/> Services Offered
              </h2>
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                {builder.services.map((service) => (
                  <span
                    key={service.id}
                    className="px-4 py-2 bg-blue-50 border border-blue-200 text-blue-800 rounded-full text-sm font-medium shadow-sm cursor-default transition duration-200 hover:bg-blue-100 hover:shadow-md"
                  >
                    {service.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Portfolio Section */}
          {/* ... (Portfolio Section JSX remains the same) ... */}
           {builder.portfolios && builder.portfolios.length > 0 && (
            <div className="mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8 flex items-center justify-center gap-3">
                 <PhotoIcon className="w-7 h-7 text-green-500"/> Portfolio Showcase
              </h2>
              <div className={`grid ${portfolioGridCols()} gap-5 sm:gap-6 justify-items-center`}>
                {builder.portfolios.map((portfolio, index) => (
                  <div key={portfolio.id || index} className="rounded-xl overflow-hidden shadow-lg border border-gray-200 group w-full bg-white transition duration-300 hover:shadow-xl aspect-w-1 aspect-h-1"> {/* Enforce aspect ratio */}
                    <img
                      src={portfolio.image_url || "/assets/placeholder-image.png"}
                      alt={`Portfolio ${index + 1}: ${portfolio.description || "Project Image"}`}
                      className="w-full h-full object-cover transition duration-500 ease-in-out group-hover:scale-105"
                      loading="lazy" // Add lazy loading
                      onError={(e) => { e.target.src = "/assets/placeholder-image.png"; }}
                    />
                     {/* Optional: Overlay on hover for description */}
                     {portfolio.description && (
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <p className="text-white text-xs leading-snug line-clamp-2">{portfolio.description}</p>
                        </div>
                     )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Enquiry Form Section - Conditionally Rendered */}
          {user && userType === 'customer' && ( // <-- ERROR WAS HERE, now corrected
            <div className="mb-6 sm:mb-8">
              <div className="rounded-xl p-6 sm:p-10 max-w-2xl mx-auto bg-gradient-to-b from-white to-gray-50 shadow-xl border border-gray-100">
                <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-800 mb-8 flex items-center justify-center gap-3">
                  <EnvelopeIcon className="w-7 h-7 text-purple-500" /> Make an Enquiry
                </h2>
                <form onSubmit={handleEnquirySubmit}>
                   {/* ... (Enquiry Form JSX remains the same) ... */}
                    <div className="space-y-5">
                  {/* Logged-in User Info (Readonly) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Your Name</label>
                      <input
                        type="text"
                        value={user?.name || ""}
                        readOnly
                        className="w-full border-gray-200 rounded-md px-3 py-2 focus:outline-none text-gray-500 bg-gray-100 cursor-not-allowed text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Your Email</label>
                      <input
                        type="email"
                        value={user?.email || ""}
                        readOnly
                        className="w-full border-gray-200 rounded-md px-3 py-2 focus:outline-none text-gray-500 bg-gray-100 cursor-not-allowed text-sm"
                      />
                    </div>
                  </div>

                  {/* Phone Number (Editable) */}
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="phoneNumber"
                      type="tel"
                      placeholder="Enter your 10-digit phone number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 text-gray-700 text-sm"
                    />
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
                      Select Service Needed <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="service"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 bg-white text-gray-700 text-sm appearance-none"
                      value={service}
                      onChange={(e) => setService(e.target.value)}
                      required
                    >
                      <option value="" disabled className="text-gray-400">-- Choose a service --</option>
                      {builder?.services && builder.services.length > 0 ? (
                        builder.services.map((svc) => (
                          <option key={svc.id} value={svc.id} className="text-black"> {/* Changed value to svc.id */}
                            {svc.name}
                          </option>
                        ))
                      ) : (
                        <option disabled className="text-gray-400">No services listed by builder</option>
                      )}
                    </select>
                  </div>

                  {/* Location Dropdowns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="district" className="block text-sm font-medium text-gray-700 mb-1">
                        Your District <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="district"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 bg-white text-gray-700 text-sm appearance-none"
                        value={selectedDistrict}
                        onChange={(e) => setSelectedDistrict(e.target.value)}
                        required
                      >
                        <option value="" disabled className="text-gray-400">-- Select your district --</option>
                        {mappedDistricts?.districts?.map((district) => (
                          <option key={district} value={district} className="text-black">{district}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="taluk" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Taluk <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="taluk"
                        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 bg-white text-gray-700 text-sm appearance-none ${
                          !selectedDistrict ? "border-gray-200 bg-gray-100 cursor-not-allowed" : "border-gray-300"
                        }`}
                        value={taluk}
                        onChange={(e) => setTaluk(e.target.value)}
                        required
                        disabled={!selectedDistrict}
                      >
                        <option value="" disabled className="text-gray-400">-- Select your taluk --</option>
                        {mappedTaluks?.taluks?.map((t) => (
                          <option key={t} value={t} className="text-black">{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Postal Code and Project Location */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Postal Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="postalCode"
                        type="text"
                        placeholder="Enter 6-digit postal code"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        required
                        maxLength="6"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 text-gray-700 text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="projectLocation" className="block text-sm font-medium text-gray-700 mb-1">
                        Your Project Location <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="projectLocation"
                        type="text"
                        placeholder="E.g., Street Name, Landmark"
                        value={projectLocation}
                        onChange={(e) => setProjectLocation(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-500 text-gray-700 text-sm"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      className={`bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold py-2.5 px-10 rounded-lg shadow-md flex items-center justify-center transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg ${
                        isSubmitting ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending Enquiry...
                        </>
                      ) : (
                        "SEND ENQUIRY"
                      )}
                    </button>
                  </div>
                </div>
                </form>
              </div>
            </div>
          )} {/* End conditional rendering for form */}

        </div>
      </div>
    </div>
  );
};

export default BuildersInfo;